import type { CadNextNotificationTypeEnumType, WorkspacePanelTypeEnumType } from '@mark43/cad-api';
import type { WorkspacePanel } from '../panels/workspace-panel';

/**
 * `SENSOR`, `SENSOR_EVENT` and `SENSOR_GRID` are client-only workspace panel types.
 *
 * None has a backend counterpart: all three are absent from `WorkspacePanelType.java`, from the
 * generated `WorkspacePanelTypeEnum`, and from `WorkspaceElf.unPersistedPanelTypes`. This module
 * exists so that every place the frontend has to pretend otherwise is one grep away, and so that
 * removing the pretence is a single-file change once the enum members land.
 *
 * What the missing enum member costs us, and what is done about it:
 *
 * - `WorkspacePanelTypeEnum[type].workspacePanelCategory` throws on an unknown type. Both call
 *   sites (`workspace-store.addPanel`, `shouldBringExistingPanelToFront`) read it through
 *   {@link getClientWorkspacePanelCategory} instead.
 * - The server would receive an enum value it cannot parse on every workspace save, which would
 *   put the whole window's persistence at risk — map filters and event panels included. So these
 *   panels are filtered out of `getPersistablePanels` client-side rather than relying on the
 *   server to strip them. Consequence: none of them survives a reload.
 *
 * The casts are deliberately the only ones — everything downstream is typed off them.
 */
export const SENSOR_PANEL = 'SENSOR' as WorkspacePanelTypeEnumType;
export const SENSOR_EVENT_PANEL = 'SENSOR_EVENT' as WorkspacePanelTypeEnumType;
export const SENSOR_GRID_PANEL = 'SENSOR_GRID' as WorkspacePanelTypeEnumType;

export const CLIENT_ONLY_PANEL_TYPES = [
    SENSOR_PANEL,
    SENSOR_EVENT_PANEL,
    SENSOR_GRID_PANEL,
];

export function isClientOnlyPanelType(workspacePanelType: WorkspacePanelTypeEnumType): boolean {
    return CLIENT_ONLY_PANEL_TYPES.includes(workspacePanelType);
}

/**
 * `WorkspacePanel`'s type parameter is constrained to `WorkspacePanelTypeEnumType | unknown`, which
 * collapses to `unknown`, so the literal resolves against `WorkspacePanelTypeToInitialProps`
 * without needing the enum member to exist.
 */
export type SensorWorkspacePanel = WorkspacePanel<'SENSOR'>;
export type SensorEventWorkspacePanel = WorkspacePanel<'SENSOR_EVENT'>;
export type SensorGridWorkspacePanel = WorkspacePanel<'SENSOR_GRID'>;

/**
 * The category a panel type behaves as. Client-only types have no enum entry to read one from, so
 * they are declared here.
 *
 * `ENTITY` is the right answer for all three: it makes `shouldBringExistingPanelToFront` return
 * true, so opening an entity that is already open focuses its panel instead of stacking a
 * duplicate.
 */
export function getClientWorkspacePanelCategory(
    workspacePanelType: WorkspacePanelTypeEnumType,
    workspacePanelTypeEnum: Record<string, { workspacePanelCategory: string } | undefined>
): string | undefined {
    if (isClientOnlyPanelType(workspacePanelType)) {
        return 'ENTITY';
    }
    return workspacePanelTypeEnum[workspacePanelType]?.workspacePanelCategory;
}

/**
 * `SENSOR_ALERT_LINKED` is published on `cadNextNotifications` when a sensor alert is linked to a
 * CAD event, and deleted on unlink. It drives the notification marker on the event panel's Sensor
 * Events tab.
 *
 * Cast because it is not in the generated enum yet: it exists in `CadNextNotificationType.java`
 * on Ari's branch, but `cad-enums` has not been regenerated since he added it. The value is the
 * enum constant's name, which is what the wire carries, so this is correct today and becomes a
 * plain reference the moment the regen lands — at which point delete this and import it.
 *
 * It is a marker, not a count: `entityId` is a long and `alertKey` is a string, and a counter
 * needs a stable immortal key.
 */
export const SENSOR_ALERT_LINKED_NOTIFICATION =
    'SENSOR_ALERT_LINKED' as CadNextNotificationTypeEnumType;
